import { useResizeObserver } from "@vueuse/core";
import { toValue } from "vue";

const DEFAULT_PREFIX = "--sp-bp-";

/**
 * Resolves a CSS length against the element it will be compared to, so `ch` and `em` are measured
 * in that element's own font.
 *
 * @param {HTMLElement} element - The observed element.
 * @param {string} length - A CSS length, e.g. `"50ch"`.
 * @returns {number} The length in pixels.
 */
function toPixels(element, length) {
  const ruler = document.createElement("div");

  Object.assign(ruler.style, { position: "absolute", visibility: "hidden", width: length, zIndex: -1 });
  element.appendChild(ruler);
  const pixels = ruler.offsetWidth;
  ruler.remove();

  return pixels;
}

function toList(breakpoints) {
  return String(breakpoints ?? "")
    .split(",")
    .map((breakpoint) => breakpoint.trim())
    .filter(Boolean);
}

/**
 * Marks an element with a class per container breakpoint it is above or below, so a stylesheet can
 * respond to the element's own size rather than the viewport's.
 *
 * For `width-breakpoints="50ch"` the element carries either `--sp-bp-width-lte-50ch` or
 * `--sp-bp-width-gt-50ch`, and with `withOrientation` also one of `--sp-bp-landscape`,
 * `--sp-bp-portrait` or `--sp-bp-square`.
 *
 * @param {import("vue").MaybeRefOrGetter<HTMLElement|null>} target - The element to observe and mark.
 * @param {Object} [options]
 * @param {import("vue").MaybeRefOrGetter<string|undefined>} [options.widthBreakpoints] - Comma-separated CSS lengths.
 * @param {import("vue").MaybeRefOrGetter<string|undefined>} [options.heightBreakpoints] - Comma-separated CSS lengths.
 * @param {import("vue").MaybeRefOrGetter<boolean>} [options.withOrientation] - Also mark the aspect ratio.
 * @param {import("vue").MaybeRefOrGetter<string>} [options.prefix] - Class prefix.
 */
export function useContainerBreakpointClasses(target, options = {}) {
  const { widthBreakpoints, heightBreakpoints, withOrientation = false, prefix = DEFAULT_PREFIX } = options;

  function markDimension(element, dimension, breakpoint, size) {
    const length = dimension === "width" ? size.inlineSize : size.blockSize;
    const withinBreakpoint = length <= toPixels(element, breakpoint);
    const classPrefix = toValue(prefix);

    element.classList.toggle(`${classPrefix}${dimension}-lte-${breakpoint}`, withinBreakpoint);
    element.classList.toggle(`${classPrefix}${dimension}-gt-${breakpoint}`, !withinBreakpoint);
  }

  function markOrientation(element, size) {
    const ratio = size.inlineSize / size.blockSize;
    const classPrefix = toValue(prefix);

    element.classList.toggle(`${classPrefix}landscape`, ratio > 1);
    element.classList.toggle(`${classPrefix}portrait`, ratio < 1);
    element.classList.toggle(`${classPrefix}square`, ratio === 1);
  }

  // `contentRect` is the content box; `offsetWidth`/`offsetHeight` the border box.
  function toSize(element, entry) {
    const isBorderBox = getComputedStyle(element).boxSizing === "border-box";
    const box = isBorderBox ? entry.borderBoxSize : entry.contentBoxSize;
    const reported = Array.isArray(box) ? box[0] : box;

    if (reported) {
      return reported;
    }

    return isBorderBox
      ? { inlineSize: element.offsetWidth, blockSize: element.offsetHeight }
      : { inlineSize: entry.contentRect.width, blockSize: entry.contentRect.height };
  }

  function mark(element, entry) {
    const size = toSize(element, entry);

    toList(toValue(widthBreakpoints)).forEach((breakpoint) => markDimension(element, "width", breakpoint, size));
    toList(toValue(heightBreakpoints)).forEach((breakpoint) => markDimension(element, "height", breakpoint, size));

    if (toValue(withOrientation)) {
      markOrientation(element, size);
    }
  }

  const observed = () => {
    const marksSomething =
      toList(toValue(widthBreakpoints)).length > 0 ||
      toList(toValue(heightBreakpoints)).length > 0 ||
      toValue(withOrientation);

    return marksSomething ? toValue(target) : null;
  };

  useResizeObserver(observed, (entries) => {
    const [entry] = entries ?? [];

    if (entry?.target) {
      mark(entry.target, entry);
    }
  });
}
